import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../layouts';
import { api } from '../lib/api';

// Only one customer exists today — DEFAULT_CUSTOMER on the core-agents side.
// The API already takes a customerName param so a switcher can be added
// later without a breaking change; not built now since there's nothing to switch between.
const CUSTOMER_NAME = 'gutermann';

export const CustomerConfig = () => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['customer-config', CUSTOMER_NAME],
    queryFn: () => api.getCustomerConfig(CUSTOMER_NAME),
    retry: false, // a 404 (no config saved yet) isn't transient — don't retry it
  });

  useEffect(() => {
    if (data) setDraft(data.raw);
  }, [data]);

  const isNotFound = error instanceof Error && error.message.includes('(404)');

  const saveMutation = useMutation({
    mutationFn: (raw: string) => api.updateCustomerConfig(CUSTOMER_NAME, raw),
    onSuccess: (result) => {
      setSuccessMessage(result.message);
      queryClient.invalidateQueries({ queryKey: ['customer-config', CUSTOMER_NAME] });
    },
  });

  const handleSave = () => {
    setSuccessMessage(null);
    saveMutation.mutate(draft);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Customer Config</h1>
          <p className="text-sm text-gray-500 mt-1">
            Edits are validated and applied live by the AI service — no redeploy needed.
            Invalid config is rejected and nothing is saved.
          </p>
        </div>

        {isLoading && (
          <div className="text-sm text-gray-500">Loading...</div>
        )}

        {error && !isNotFound && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            {error instanceof Error ? error.message : 'Failed to load config'}
          </div>
        )}

        {isNotFound && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm mb-4">
            No saved config for '{CUSTOMER_NAME}' yet — paste the current config.yaml content
            below and save once to get started.
          </div>
        )}

        {saveMutation.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 whitespace-pre-wrap">
            {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">
            {successMessage}
          </div>
        )}

        {(data || isNotFound) && !isLoading && (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              className="w-full h-[32rem] border rounded-lg p-3 font-mono text-xs leading-relaxed"
              placeholder="customer: ...&#10;llm: ...&#10;# paste config.yaml content here"
            />
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending || !draft.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              {data?.updatedAt && (
                <span className="text-xs text-gray-500">
                  Last saved {new Date(data.updatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};
